import {
    Component,
    EventEmitter,
    Input,
    Output,
    computed,
    effect,
    inject,
    signal
} from "@angular/core";
import { FlexLayoutModule } from "@angular/flex-layout";

import { DeviceService } from "@app/shared/services/device.service";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { CompanyEntity } from "@arch/domain/entity/company.entity";
import { PointEntity } from "@arch/domain/entity/point.entity";
import { SerieEntity } from "@arch/domain/entity/serie.entity";
import { VisualizationEntity } from "@arch/domain/entity/visualization.entity";
import { GetIndicatorComparationsController } from "@arch/presentation/controllers/get-indicator-comparations.controller";
import { GetSeriesByIdsController } from "@arch/presentation/controllers/get-series-by-ids.controller";
import { GetVisualizationDataController } from "@arch/presentation/controllers/get-visualization-data.controller";
import { ChartHistoricComponent } from "@shared/components/chart-historic/chart-historic.component";
import { HighchartsRenderComponent } from "@shared/components/highcharts-render/highcharts-render.component";
import { SpecialChartRenderComponent } from "@shared/components/special-charts/special-chart-render/special-chart-render.component";
import { IndicatorValuesComponent } from "@shared/components/visualization/indicator-values/indicator-values.component";
@Component({
    selector: "app-visualization",
    imports: [
        ChartHistoricComponent,
        HighchartsRenderComponent,
        FlexLayoutModule,
        IndicatorValuesComponent,
        SpecialChartRenderComponent
    ],
    templateUrl: "./visualization.component.html",
    styleUrl: "./visualization.component.scss"
})
export class VisualizationComponent {
    @Input({ required: true }) loading: boolean;
    @Input({ required: true }) set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    get options() {
        return this.optionsSignal();
    }
    private optionsSignal = signal<VisualizationOptionsType>(null);
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) absoluteChartArea: boolean = false;
    @Input({ required: false }) isDrilldown: boolean = false;
    @Input({ required: false }) suffixIndicatorValues?: string;
    @Input({ required: false }) isAccumulated: boolean = false;

    @Output() handlePoints = new EventEmitter<PointEntity[]>();
    private readonly deviceService = inject(DeviceService);
    public readonly isMobile = this.deviceService.getIsMobile();

    public points: PointEntity[];
    public series: SerieEntity[];
    private pointsSignal = signal<PointEntity[]>([]);

    private readonly getVisualizationDataController = inject(
        GetVisualizationDataController
    );
    private readonly getSeriesByIdsController = inject(
        GetSeriesByIdsController
    );
    private readonly getIndicatorComparationsController = inject(
        GetIndicatorComparationsController
    );

    public hasValidData = computed(() => {
        if (this.visualization.isDrilldown) return true;
        const points = this.pointsSignal();
        return this.hasPointsData(points) && this.hasNonZeroValuesData(points);
    });

    constructor() {
        effect(() => {
            if (this.options) {
                const data = this.getVisualizationDataController.execute({
                    company: this.company,
                    options: this.options,
                    visualization: this.visualization,
                    extOptions: {
                        isAccumulated: this.isAccumulated
                    }
                });
                this.options.dates = data.dates;
                this.series = data.series;
                this.points = data.points;
                this.handlePoints.emit(this.points);
                this.pointsSignal.set(data.points);
                const seriesIds = this.visualization.getSeriesIdsComparation();
                const seriesComparations =
                    this.getSeriesByIdsController.execute(seriesIds);
                const comparations = this.getComparations(seriesComparations);
                this.visualization.setComparationsValues(comparations);
                if (this.options.goals && data.goalsSeries?.length) {
                    this.visualization.setGoalsSeries(data.goalsSeries);
                }
            }
        });
    }

    private hasPointsData(points: PointEntity[]): boolean {
        return points && points.length > 0;
    }

    private hasNonZeroValuesData(points: PointEntity[]): boolean {
        if (!points || points.length === 0) {
            return false;
        }

        return points.some((point) => {
            const value = point.props.m?.v || point.props.m?.f || 0;
            return value !== 0 && value !== null && value !== undefined;
        });
    }

    private getComparations(series: SerieEntity[]) {
        const comparations = this.getIndicatorComparationsController.execute({
            company: this.company,
            series,
            options: this.options,
            extOptions: {
                isAccumulated: this.isAccumulated
            }
        });
        return comparations;
    }
}
